import { useAppSelector } from '@/Application/Hooks/Redux';
import type { Cart } from '@/modules/cart/domain/Cart';

import { useGiftCardData } from '../../hooks/use-gift-card-data';

import { TPaymentMethodId } from './interfaces';
import {
  containsEasyProduct,
  containsIntangible,
  containsIntangibleMarketplace,
  containsIntangibleParis
} from '.';

export const useEnableAccordion = () => {
  const { data: cart } = useAppSelector((state) => state.cart);
  const { availablePayments } = useAppSelector((state) => state.billing);

  const { cartHasGiftCardPayment, canGiftCardPayTotalAmount } =
    useGiftCardData();

  const cartHasPayedWithGiftCard = canGiftCardPayTotalAmount();
  const hasInvoice = !!cart?.billingAddress?.isInvoice;
  const cencoPayWallet = availablePayments?.wallets?.find(
    (wallet) => wallet.methodId === 'CENCOPAY'
  );

  const isCencopayEnabled = (cart: Cart): boolean => {
    const isEasyMarketplaceEnabled = cencoPayWallet?.canPayWithMkpEasyProducts;
    const hasEasyProduct = containsEasyProduct(cart);

    const isEasyMkpDisabled = !isEasyMarketplaceEnabled && hasEasyProduct;

    if (!cart || isEasyMkpDisabled || cartHasPayedWithGiftCard) return false;

    const hasGiftCard = cartHasGiftCardPayment();

    if (hasInvoice || hasGiftCard) return false;
    return true;
  };

  const isSavedCardsEnabled = (cart: Cart): boolean => {
    if (!cart || cartHasPayedWithGiftCard) return false;
    return true;
  };

  const isCATEnabled = (cart: Cart): boolean => {
    if (!cart || cartHasPayedWithGiftCard) return false;
    return true;
  };

  const isTCOMEnabled = (cart: Cart): boolean => {
    if (!cart || cartHasPayedWithGiftCard) return false;
    return true;
  };

  const isWebPayEnabled = (cart: Cart): boolean => {
    if (!cart || cartHasPayedWithGiftCard) return false;
    return true;
  };

  const isGiftCardEnabled = (cart: Cart): boolean => {
    if (!cart) return false;

    const hasEasyProduct = containsEasyProduct(cart);
    const hasIntangibleMarketplace = containsIntangibleMarketplace(cart);
    const hasIntangibleParis = containsIntangibleParis(cart);

    if (hasInvoice) return false;
    if (hasEasyProduct) return false;
    if (hasIntangibleMarketplace) return false;
    if (hasIntangibleParis) return false;

    return true;
  };

  const isClubParisEnabled = (cart: Cart): boolean => {
    if (!cart) return false;
    return true;
  };

  const isInvoiceEnabled = (cart: Cart): boolean => {
    if (!cart) return false;

    const hasIntangible = containsIntangible(cart);
    const hasGiftCard = cartHasGiftCardPayment();

    return !hasIntangible && !hasGiftCard;
  };

  const getPaymentMethodEnabled = (
    paymentMethod: TPaymentMethodId
  ): boolean => {
    if (!cart) return false;

    const isCencoPay = paymentMethod === 'CENCOPAY';
    const isSavedCards = paymentMethod === 'SAVED_CARDS';
    const isCAT = paymentMethod === 'EPAY_CENCOSUD_TCOM';
    const isTCOM = paymentMethod === 'EPAY_TCOM';
    const isWebPay = paymentMethod === 'WEBPAY';
    const isGiftCard = paymentMethod === 'GIFT_CARD';
    const isClub = paymentMethod === 'CLUB';
    const isInvoice = paymentMethod === TPaymentMethodId.INVOICE;

    if (isCencoPay) return isCencopayEnabled(cart);

    if (isSavedCards) return isSavedCardsEnabled(cart);

    if (isCAT) return isCATEnabled(cart);

    if (isTCOM) return isTCOMEnabled(cart);

    if (isWebPay) return isWebPayEnabled(cart);

    if (isGiftCard) return isGiftCardEnabled(cart);

    if (isClub) return isClubParisEnabled(cart);

    if (isInvoice) return isInvoiceEnabled(cart);

    return true;
  };

  return { getPaymentMethodEnabled };
};
